// Phase 82h — Athlete homepage hook (combine tournaments list + live matches list).
// Sources: GET /api/public/tournaments + GET /api/public/matches?status=in_progress
//          + GET /api/athlete/home-stats (Phase 83z.12 — real user stats).
// Phase 83z.74 — Adds real quick-ranking data (top 10 region with global fallback).
import { useQuery } from "@tanstack/react-query";
import { publicApi } from "../lib/publicApi";
import { api } from "../lib/api";
import { edgeGet } from "../lib/edgeApi";
import { useAuth } from "../contexts/AuthContext";
import { mapTournamentsList } from "../lib/tournamentMapper";
import { mapHomeMatches } from "../lib/homeMapper";
import {
  mockHeroTournaments,
  mockUpcoming,
  mockLiveMatches,
  mockRanking,
} from "../lib/homeMockData";

const USE_REAL =
  (process.env.REACT_APP_USE_REAL_HOME || "true").toLowerCase() !== "false";

function tournamentToHero(t, registeredIds) {
  // mockHeroTournaments shape: {id, subtitle, title, date, location, daysLeft}
  return {
    id: t.id,
    slug: t.slug,
    subtitle: t.subtitle || t.type || "PICKLEAGUE",
    title: (t.title || "").toUpperCase(),
    date: t.date,
    location: t.location || t.province || "",
    daysLeft: t.daysLeft ?? 0,
    // Phase 83z.12 — Surface real data for the prize badge + CTA button.
    totalPrizePool: Number(t.totalPrizePool || t.total_prize_pool || 0),
    registrationStatus: t.registrationStatus || t.registration_status, // open/closed/ended
    isRegistered: Boolean(registeredIds?.includes(t.id)),
    // Phase 84.M.28 — Pass through UI status so HeroBanner có thể hiển thị
    // badge "ĐANG DIỄN RA" pulsing đỏ khi tournament đang live.
    status: t.status, // "upcoming" | "live" | "finished"
  };
}

function tournamentToUpcoming(t) {
  // mockUpcoming shape: {id, title, date, location, currentReg, maxReg, status, shortLogo, banner, logo}
  const max = t.registration?.max || 0;
  const cur = t.registration?.current || 0;
  let regStatus = "open";
  if (max && cur >= max) regStatus = "full";
  else if (max && cur / max >= 0.8) regStatus = "closing";
  // Phase 90.C.home-upcoming-media — Expose banner + logo từ tournament card
  // để UpcomingCard render ảnh thật (không còn cần shortLogo placeholder).
  // Ưu tiên t.banner / t.logo do mapTournamentToCard đã map từ banner_url /
  // logo_url. Nếu là fallback path chứa "/home-assets/", coi như KHÔNG có
  // ảnh (fallback default không có ý nghĩa cho upcoming card).
  const _isDefault = (u) => typeof u === "string" && u.startsWith("/home-assets/");
  const banner = t.banner && !_isDefault(t.banner) ? t.banner : null;
  const logo = t.logo && !_isDefault(t.logo) ? t.logo : null;
  return {
    id: t.id,
    title: t.title,
    date: t.date,
    location: t.location || "",
    currentReg: cur,
    maxReg: max,
    status: regStatus,
    shortLogo: (t.title || "GIẢI").split(" ")[0].slice(0, 4).toUpperCase(),
    banner,
    logo,
  };
}

export default function useHomeData() {
  const { user } = useAuth();
  const myId = user?.id;
  const myRegion = user?.region || user?.province_code || null;
  const tournamentsQ = useQuery({
    queryKey: ["public", "tournaments", "home"],
    enabled: USE_REAL,
    queryFn: async () => {
      // Phase 84.M.27 — Đồng bộ filter với /athlete/tournaments tab "Sắp diễn ra":
      // backend không hỗ trợ multi-status, nên fetch ALL (limit 60) rồi
      // filter client-side `upcoming || live` để khớp 100% với tournaments page.
      // Trước đây `status=upcoming` only → bỏ qua giải đang LIVE → home thiếu giải.
      const raw = await publicApi.listTournaments({ limit: 60 });
      const mapped = mapTournamentsList(raw);
      return mapped.filter((t) => t.status === "upcoming" || t.status === "live");
    },
    staleTime: 60_000,
    retry: 0,
  });

  const liveMatchesQ = useQuery({
    queryKey: ["public", "matches", "live"],
    enabled: USE_REAL,
    queryFn: async () => {
      const raw = await edgeGet("/public/matches", {
        policyKey: "m_list", params: { status: "in_progress", limit: 6 } })
        .then((r) => r.data);
      return mapHomeMatches(raw);
    },
    staleTime: 5_000,
    retry: 0,
  });

  // Phase 83z.71 — Fallback "trận đấu đang live" sang CHUNG KẾT cuối cùng
  // gần nhất khi không có trận live thực. Chỉ enabled khi live = empty.
  const finalRecapQ = useQuery({
    queryKey: ["public", "matches", "latest-final"],
    enabled: USE_REAL && !!liveMatchesQ.data && liveMatchesQ.data.length === 0,
    queryFn: async () => {
      const raw = await edgeGet("/public/matches", {
        policyKey: "m_list",
          params: {
            status: "completed",
            phase: "knockout",
            round_name: "CK",
            limit: 12,
            sort: "-completed_at",
          },
        })
        .then((r) => r.data);
      return mapHomeMatches(raw);
    },
    staleTime: 60_000,
    retry: 0,
  });

  // Phase 83z.12 — Real homepage stats for the logged-in athlete.
  // PR-B: chỉ gọi khi ĐÃ đăng nhập — anonymous từng bắn 401 → kích
  // /auth/refresh vô ích mỗi page-load homepage.
  const statsQ = useQuery({
    queryKey: ["athlete", "home-stats"],
    enabled: USE_REAL && !!myId,
    queryFn: () => api.get("/athlete/home-stats").then((r) => r.data).catch(() => null),
    staleTime: 60_000,
    retry: 0,
  });

  // Phase 83z.74 — Real quick ranking (region with global fallback).
  const quickRankingQ = useQuery({
    queryKey: ["public", "quick-ranking", myRegion, myId],
    enabled: USE_REAL,
    queryFn: () =>
      edgeGet("/public/quick-ranking", {
        policyKey: "quick_ranking",
          params: { region: myRegion || undefined, me: myId || undefined, limit: 10 },
        })
        .then((r) => r.data)
        .catch(() => null),
    staleTime: 60_000,
    retry: 0,
  });

  const rankingReal = quickRankingQ.data?.items?.length
    ? quickRankingQ.data.items.map((u) => ({
        id: u.id,
        name: u.full_name || u.username,
        username: u.username,
        avatar: u.avatar_url,
        rank: u.rank,
        score: u.rating,
        is_me: !!u.is_me,
      }))
    : null;

  // Decide what to expose
  const tournamentsReal =
    tournamentsQ.data && tournamentsQ.data.length > 0 ? tournamentsQ.data : null;
  const liveReal =
    liveMatchesQ.data && liveMatchesQ.data.length > 0 ? liveMatchesQ.data : null;
  // Phase 83z.71 — Khi không có trận live thực → dùng chung kết gần nhất
  // làm "trận tâm điểm" thay vì mock cũ.
  // Phase 85.A.311 — Carousel: lấy TẤT CẢ trận CK của ĐÚNG 1 giải gần nhất
  // (giải có trận CK mới nhất; mỗi nội dung 1 trận CK) để chạy carousel.
  const finalRecapReal = (() => {
    const all = finalRecapQ.data;
    if (!all || all.length === 0) return null;
    const topTid = all[0].tournamentId;
    const sameTournament = topTid
      ? all.filter((m) => m.tournamentId === topTid)
      : [all[0]];
    return sameTournament.length > 0 ? sameTournament : [all[0]];
  })();

  const registeredIds = statsQ.data?.registered_tournament_ids || [];
  // Phase 84.M.27 — Hero giữ slice 5 (carousel-style) nhưng "Sắp diễn ra"
  // list hiển thị TOÀN BỘ (giống tournaments page) thay vì cap 6.
  // Phase 84.M.40 — KHÔNG fall-back sang mock data nữa: hiển thị empty array
  // để UI render empty state thật thay vì giải mock "TÂY NGUYÊN OPEN 2026"
  // gây hiểu lầm trên guest/PROD.
  // v16.23 (Feb 2026) — Sort theo ngày diễn ra GẦN NHẤT (ASC) trước khi
  // xẻ ra Hero + Upcoming, để trang chủ show giải sắp tới trước.
  const tournamentsSorted = tournamentsReal
    ? [...tournamentsReal].sort((a, b) => {
        const ta = new Date(a?.raw?.start_date || a?.raw?.registration_open_at || 0).getTime();
        const tb = new Date(b?.raw?.start_date || b?.raw?.registration_open_at || 0).getTime();
        const aa = Number.isFinite(ta) && ta > 0 ? ta : Infinity;
        const bb = Number.isFinite(tb) && tb > 0 ? tb : Infinity;
        return aa - bb;
      })
    : null;
  const heroTournaments = tournamentsSorted
    ? tournamentsSorted.slice(0, 5).map((t) => tournamentToHero(t, registeredIds))
    : [];
  const upcomingTournaments = tournamentsSorted
    ? tournamentsSorted.map(tournamentToUpcoming)
    : [];
  const liveMatches = liveReal || finalRecapReal || [];

  const heroFromFallback = !tournamentsReal;
  // Phase 83z.71 — Trận chung kết cuối cùng KHÔNG phải mock — chỉ flag là
  // fallback (badge "Mock") khi cả live thực lẫn final recap đều rỗng.
  // Phase 84.M.40 — Vì đã bỏ mock fallback, fallback flag chỉ còn dùng cho
  // hiển thị empty state. Không show badge "Mock" nữa.
  const liveFromFallback = !liveReal && !finalRecapReal;
  const isFinalRecap = !liveReal && !!finalRecapReal;

  return {
    heroTournaments,
    upcomingTournaments,
    liveMatches,
    ranking: rankingReal || mockRanking,
    rankingScope: quickRankingQ.data?.scope || "global",
    rankingMyExtra: quickRankingQ.data?.me_extra || null,
    rankingFromFallback: !rankingReal,
    myId,
    homeStats: statsQ.data || null,
    isHeroFallback: heroFromFallback,
    isLiveFallback: liveFromFallback,
    isFinalRecap,
    isLoading: tournamentsQ.isLoading || liveMatchesQ.isLoading,
  };
}
